/* ──────────────────────────────────────────────────────────────
   src/pages/admin/manager/ProfesorDatosPage.tsx
   Gestión de ficha del profesor – ahora con teléfono + fechas
   + botón de exportación y barra de búsqueda amplia
   ────────────────────────────────────────────────────────────── */

import { useState, useEffect } from 'react';
import {
  Tabs,
  Tab,
  Box,
  Stack,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField,
  Chip,
  Button,
  Snackbar,
  Alert,
  Checkbox,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import api from '../../../api/axios';
import { useTranslation } from 'react-i18next';
import GenericExportDialog, { Field } from '../../../components/GenericExportDialog';
import DownloadIcon from '@mui/icons-material/Download';
import { useSearchParams } from 'react-router-dom';

/* hooks catálogos / pivotes */
import { useProfesores } from '../../../hooks/useProfesores';
import { usePivot } from '../../../hooks/usePivots';
import { useAsignaturas } from '../../../hooks/useAsignaturas';
import { useCentros } from '../../../hooks/useCentros';
import { useFormatos } from '../../../hooks/useFormatos';
import { useIdiomas } from '../../../hooks/useIdiomas';
import { useTipos } from '../../../hooks/useTiposAsignatura';

/* ═════════════════════════════════════════════════════════════ */
/* helper SelectorPivot                                          */
/* ═════════════════════════════════════════════════════════════ */
function SelectorPivot<T extends { id: number; nombre: string }>({
  label,
  options,
  selected,
  onAdd,
  onRemove,
}: {
  label: string;
  options: T[];
  selected: T[];
  onAdd: (id: number) => void;
  onRemove: (id: number) => void;
}) {
  const [pendientes, setPendientes] = useState<T[]>([]);
  const { t } = useTranslation<'translation'>();

  return (
    <Stack spacing={2}>
      <Autocomplete
        multiple
        disableCloseOnSelect
        sx={{ maxWidth: 400 }}
        options={options.filter(
          (o) => !selected.some((s) => s.id === o.id) && !pendientes.some((p) => p.id === o.id),
        )}
        value={pendientes}
        onChange={(_, nuevas) => {
          nuevas.filter((n) => !selected.some((s) => s.id === n.id)).forEach((n) => onAdd(n.id));
          setPendientes([]);
        }}
        getOptionLabel={(o) => o.nombre}
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox checked={selected} sx={{ mr: 1 }} />
            {option.nombre}
          </li>
        )}
        renderTags={() => null}
        renderInput={(p) => <TextField {...p} label={t(`profesorDatos.add.${label}`)} />}
      />

      <Stack direction="row" spacing={1} flexWrap="wrap">
        {selected.length === 0 ? (
          <Typography color="text.secondary">{t(`profesorDatos.empty.${label}`)}</Typography>
        ) : (
          selected.map((s) => <Chip key={s.id} label={s.nombre} onDelete={() => onRemove(s.id)} />)
        )}
      </Stack>
    </Stack>
  );
}

/* ═════════════════════════════════════════════════════════════ */
/* página                                                        */
/* ═════════════════════════════════════════════════════════════ */
export default function ProfesorDatosPage() {
  /* 1. profesor seleccionado */
  const { rows: profesores, loading: loadingProf } = useProfesores();
  const [profSel, setProfSel] = useState<{ id: number; nombre: string } | null>(null);
  const { t } = useTranslation<'translation'>();
  const [searchParams, setSearchParams] = useSearchParams();

  /* 2. catálogos */
  const { rows: asign } = useAsignaturas();
  const { rows: centros } = useCentros();
  const { rows: formatos } = useFormatos();
  const { rows: idiomas } = useIdiomas();
  const { rows: tipos } = useTipos();

  /* 3. pivotes */
  const pvAsign = usePivot('/profesores', profSel?.id ?? 0, 'asignaturas');
  const pvCent = usePivot('/profesores', profSel?.id ?? 0, 'centros');
  const pvForm = usePivot('/profesores', profSel?.id ?? 0, 'formatos');
  const pvIdi = usePivot('/profesores', profSel?.id ?? 0, 'idiomas');
  const pvTipos = usePivot('/profesores', profSel?.id ?? 0, 'tipos');

  /* 4. pestañas */
  const [tab, setTab] = useState(0);

  /* 5. form */
  type BasicosForm = {
    horasSemanales: number;
    comentarios: string;
    telefono: string;
    fechaIncorporacion: string;
    fechaCumpleanos: string;
    correo: string;
  };
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, errors },
  } = useForm<BasicosForm>({
    defaultValues: {
      horasSemanales: 0,
      comentarios: '',
      telefono: '',
      fechaIncorporacion: '',
      fechaCumpleanos: '',
      correo: '',
    },
  });

  /* 6. snackbar */
  const [toast, setToast] = useState<{
    open: boolean;
    msg: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    msg: '',
    severity: 'success',
  });

  /* 7. exportación */
  const [openExport, setOpenExport] = useState(false);
  const exportFields: Field[] = [
    { key: 'userId', label: 'ID' },
    { key: 'nombreCompleto', label: 'Nombre' },
    { key: 'email', label: 'Email' },
    { key: 'telefono', label: 'Teléfono' },
    { key: 'horasSemanales', label: 'Horas/semana' },
    { key: 'fechaIncorporacion', label: 'Fecha incorporación' },
    { key: 'fechaCumpleanos', label: 'Cumpleaños' },
    { key: 'idiomas', label: 'Idiomas' },
    { key: 'tipos', label: 'Tipos de asignatura' },
    { key: 'comentarios', label: 'Comentarios' },
  ];

  /* cargar datos básicos al cambiar de profesor */
  useEffect(() => {
    if (!profSel) return;

    api.get(`/profesores/${profSel.id}/datos`).then(({ data }) =>
      reset({
        horasSemanales: data.horasSemanales ?? 0,
        comentarios: data.comentarios ?? '',
        telefono: data.telefono ?? '',
        fechaIncorporacion: data.fechaIncorporacion ?? '',
        fechaCumpleanos: data.fechaCumpleanos ?? '',
        /* aquí prueba los nombres que maneje tu API */
        correo:
          data.correo ??
          data.email ??
          data.usuario?.email ?? //  ⇦ ejemplo habitual
          '',
      }),
    );
  }, [profSel, reset]);

  /* preselección desde query param (profesorId) */
  useEffect(() => {
    if (profSel || !profesores.length) return;
    const idParam = Number(searchParams.get('profesorId'));
    if (!Number.isFinite(idParam) || !idParam) return;
    const found = profesores.find((p) => p.id === idParam);
    if (found) {
      setProfSel({ id: found.id, nombre: found.nombre });
    }
  }, [profSel, profesores, searchParams]);

  /* sincroniza query param al cambiar profesor */
  useEffect(() => {
    const current = searchParams.get('profesorId');
    const desired = profSel?.id ? String(profSel.id) : null;
    if (current === desired || (!current && !desired)) return;

    const next = new URLSearchParams(searchParams);
    if (desired) next.set('profesorId', desired);
    else next.delete('profesorId');
    setSearchParams(next, { replace: true });
  }, [profSel, searchParams, setSearchParams]);

  /* guardar */
  const saveBasicos = handleSubmit(async ({ correo, ...payload }) => {
    try {
      await api.patch(`/profesores/${profSel?.id}/datos`, payload);

      setToast({
        open: true,
        msg: t('profesorDatos.toastGuardado'),
        severity: 'success',
      });

      /* volvemos a poner TODOS los datos, incluido el correo */
      reset({ ...payload, correo });
    } catch (err) {
      setToast({
        open: true,
        msg: t('profesorDatos.toastErrorDatos'),
        severity: 'error',
      });
    }
  });

  /* ────────────────────────── render ────────────────────────── */
  return (
    <Box>
      <Typography variant="h5" mb={3}>
        {t('profesorDatos.title')}
      </Typography>

      {/* selector profesor + botón exportar */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Autocomplete
          loading={loadingProf}
          options={profesores}
          getOptionLabel={(p) => p.nombre}
          onChange={(_, v) => setProfSel(v)}
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label={t('profesorDatos.selectLabel')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingProf && <CircularProgress size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{
            flexGrow: 1,
            minWidth: { xs: 260, sm: 420 },
            maxWidth: 800, // más ancho para que quepa el nombre completo
          }}
        />

        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => setOpenExport(true)}>
          EXPORTAR ACTIVOS
        </Button>
      </Stack>

      {!profSel && (
        <Typography color="text.secondary">{t('profesorDatos.instrucciones')}</Typography>
      )}

      {profSel && (
        <>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
            <Tab label={t('profesorDatos.tabs.generales')} />
            <Tab label={t('profesorDatos.tabs.asignaturas')} />
            <Tab label={t('profesorDatos.tabs.centros')} />
            <Tab label={t('profesorDatos.tabs.formatos')} />
            <Tab label={t('profesorDatos.tabs.idiomas')} />
            <Tab label={t('profesorDatos.tabs.tipos')} />
          </Tabs>

          {/* ─────────── Generales ─────────── */}
          {tab === 0 && (
            <Stack spacing={3} sx={{ maxWidth: 700 }}>
              <TextField
                type="number"
                label={t('profesorDatos.horasLabel')}
                inputProps={{ min: 1, step: 1 }}
                {...register('horasSemanales', {
                  valueAsNumber: true,
                  required: true,
                  min: 1,
                })}
                error={!!errors.horasSemanales}
              />

              <TextField
                label={t('profesorDatos.correoLabel')}
                value={watch('correo')}
                variant="filled"
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  '& .MuiInputBase-input': { pointerEvents: 'none' },
                }}
                fullWidth
              />

              <TextField
                type="tel"
                label={t('profesorDatos.telefonoLabel')}
                InputLabelProps={{ shrink: true }} // ← siempre arriba; se ve el número sin foco
                {...register('telefono', { required: true, maxLength: 20 })}
                error={!!errors.telefono}
              />

              <TextField
                type="date"
                label={t('profesorDatos.fechaIncorporacionLabel')}
                InputLabelProps={{ shrink: true }}
                {...register('fechaIncorporacion', { required: true })}
                error={!!errors.fechaIncorporacion}
              />

              <TextField
                type="date"
                label={t('profesorDatos.fechaCumpleLabel')}
                InputLabelProps={{ shrink: true }}
                {...register('fechaCumpleanos', { required: true })}
                error={!!errors.fechaCumpleanos}
              />

              <TextField
                label={t('profesorDatos.comentariosLabel')}
                multiline
                minRows={3}
                {...register('comentarios')}
                error={!!errors.comentarios}
              />

              <Button
                variant="contained"
                onClick={saveBasicos}
                disabled={!isDirty}
                sx={{ alignSelf: 'flex-start' }}
              >
                {t('common.save')}
              </Button>
            </Stack>
          )}

          {/* ─────────── resto de pestañas (sin cambios) ─────────── */}
          {tab === 1 && (
            <SelectorPivot
              label="asignatura"
              options={asign}
              selected={pvAsign.rows}
              onAdd={pvAsign.add}
              onRemove={pvAsign.remove}
            />
          )}

          {tab === 2 && (
            <SelectorPivot
              label="centro"
              options={centros}
              selected={pvCent.rows.map((r: any) => ({
                id: r.idCentro,
                nombre: r.centro?.nombre ?? String(r.idCentro),
              }))}
              onAdd={pvCent.add}
              onRemove={pvCent.remove}
            />
          )}

          {tab === 3 && (
            <SelectorPivot
              label="formato"
              options={formatos.map((f) => ({
                id: f.id,
                nombre: f.nombreFormato,
              }))}
              selected={pvForm.rows.map((r: any) => ({
                id: r.idFormato,
                nombre: r.formato?.nombreFormato ?? '',
              }))}
              onAdd={pvForm.add}
              onRemove={pvForm.remove}
            />
          )}

          {tab === 4 && (
            <SelectorPivot
              label="idioma"
              options={idiomas}
              selected={pvIdi.rows}
              onAdd={pvIdi.add}
              onRemove={pvIdi.remove}
            />
          )}

          {tab === 5 && (
            <SelectorPivot
              label="tipo"
              options={tipos}
              selected={pvTipos.rows.map((r: any) => ({
                id: r.id || r.tipo?.id,
                nombre: r.tipo?.descripcion ?? r.descripcion ?? '',
              }))}
              onAdd={pvTipos.add}
              onRemove={pvTipos.remove}
            />
          )}
        </>
      )}

      {/* toast global */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast({ ...toast, open: false })}
        >
          {toast.msg}
        </Alert>
      </Snackbar>

      {/* diálogo de exportación */}
      <GenericExportDialog
        open={openExport}
        onClose={() => setOpenExport(false)}
        endpoint="/profesores/export"
        entityName="profesores-activos"
        fields={exportFields}
      />
    </Box>
  );
}
